import React, {
  useRef,
  useCallback,
  useMemo,
  useState,
} from 'react';
import * as _ from 'lodash';
import classNames from 'classnames';
import styles from '../../styles/views/stages/Stages.scss';
import { __, dateFrom, getLocalizedListValue } from '../../core/utils';
import BaseTable from 'react-base-table';
import AutoSizer from 'react-virtualized-auto-sizer';
import { withRouter } from 'react-router';
import ExpandIcon from './helpers/ExpandIcon';
import UncheckedIcon from '../../assets/images/unchecked-15x15.svg';
import NeutralIcon from '../../assets/images/neutral-15x15.svg';
import CheckedIcon from '../../assets/images/checked-15x15.svg';
import Tooltip from '../../components/Tooltip';
import NumericInput, { formatNumber } from './helpers/NumericInput';
import moment from 'moment';
import WarningIcon from '../../assets/images/tender-icon-warning-16x16.svg';

const formatBackendDate = (date) => {
  return moment(date, 'DD.MM.YYYY', true).format('YYYY-MM-DD');
};

const ProjectPlanningTable = ({
  match,
  onExpandedRowsChange,
  focusedId,
  onFocusChange,
  expandedRows,
  data,
  languageId,
  isProjectAdministrator,
  onSubstageUpdate,
}) => {
  const myRef = useRef();
  const [checked, setChecked] = useState([]);
  const stage_statuses = [
    {
      value: 'not-started',
      label: __('stages.status.not-started'),
      color: '#9C27B0',
    },
    {
      value: 'started',
      label: __('stages.status.started'),
      color: '#5777FC',
    },
    {
      value: 'finished',
      label: __('stages.status.finished'),
      color: '#ADD42B',
    },
    {
      value: 'closed',
      label: __('stages.status.closed'),
      color: '#009688',
    },
    {
      value: 'reopened',
      label: __('stages.status.reopened'),
      color: '#303F9F',
    },
    {
      value: 'canceled',
      label: __('stages.status.canceled'),
      color: '#CCCDD1',
    },
  ];

  const getDeepIds = useCallback((rows) => {
    const ids = [];

    const process = (currentRows) =>
      _.each(currentRows, (row) => {
        row.id != 'UN' && _.isEmpty(row.children) && ids.push(row.id);

        !_.isEmpty(row.children) && process(row.children);
      });

    process(rows);

    return ids;
  }, []);

  const final_data = useMemo(() => {
    const process = (rows) =>
      _.map(rows, (row) => {
        let is_checked = false;

        if (_.isEmpty(row.children)) {
          if (_.includes(checked, row.id)) {
            is_checked = true;
          }
        } else {
          const child_ids = getDeepIds(row.children);
          const checked_child_ids = _.intersection(checked, child_ids);

          if (checked_child_ids.length != 0) {
            if (child_ids.length == checked_child_ids.length) {
              is_checked = true;
            } else {
              is_checked = 'neutral';
            }
          }
        }

        return {
          ...row,
          checked: is_checked,
          children: _.isEmpty(row.children) ? undefined : process(row.children),
        };
      });

    return process(data);
  }, [data, checked, getDeepIds]);

  const data_ids = useMemo(() => getDeepIds(final_data), [final_data, getDeepIds]);

  const _handleCheckbox = useCallback(
    (id) => {
      const target_ids = _.filter(data_ids, (child_id) =>
        _.startsWith(child_id, id)
      );

      const intersected = _.intersection(target_ids, checked).length;

      if (intersected == 0 || intersected != target_ids.length) {
        setChecked(_.union(checked, target_ids));
      } else {
        setChecked(_.difference(checked, target_ids));
      }
    },
    [data_ids, checked]
  );

  const _handleMasterCheckbox = useCallback(() => {
    if (data_ids.length == checked.length && checked.length !== 0) {
      setChecked([]);
    } else {
      setChecked(data_ids);
    }
  }, [data_ids, checked]);

  const _handleMasterExpand = (expanded_rows) => {
    onExpandedRowsChange(expanded_rows);
    myRef?.current?.setExpandedRowKeys(expanded_rows);
  };

  const headerRenderer = ({ headerIndex, cells, columns }) => {
    if (headerIndex === 0) {
      return _.map(columns, (column, i) => {
        if (column.key === 'name') {
          const levels = {
            2: [],
          };

          _.each(data, (group) => {
            !_.isEmpty(group.children) && levels[2].push(group.id);
          });

          const active = {};

          for (let i = 1; i <= 2; i++) {
            active[i] = _.every(levels[i], (id) =>
              _.includes(expandedRows, id)
            );
          }

          return React.cloneElement(
            cells[i],
            null,
            <>
              <div className='BaseTable__header-cell-text'>
                {checked.length === data_ids.length && checked.length !== 0 && (
                  <CheckedIcon
                    className={classNames(styles.checked, styles.master)}
                    onClick={_handleMasterCheckbox}
                  />
                )}
                {checked.length === 0 && (
                  <UncheckedIcon
                    className={classNames(
                      styles.unchecked,
                      styles.master,
                      checked.length > 0 && styles.visible
                    )}
                    onClick={_handleMasterCheckbox}
                  />
                )}
                {checked.length !== data_ids.length && checked.length !== 0 && (
                  <NeutralIcon
                    className={classNames(styles.neutral, styles.master)}
                    onClick={_handleMasterCheckbox}
                  />
                )}
              </div>
              <div className={styles.masterActions}>
                <Tooltip text='Show group-level'>
                  <span
                    className={classNames(styles.active)}
                    onClick={() => _handleMasterExpand([])}
                  >
                    1
                  </span>
                </Tooltip>
                <Tooltip text={_.isEmpty(levels[2]) ? '' : 'Show type-level'}>
                  <span
                    className={classNames(
                      active[2] && styles.active,
                      _.isEmpty(levels[2]) && styles.disabled
                    )}
                    onClick={() => _handleMasterExpand(levels[2])}
                  >
                    2
                  </span>
                </Tooltip>
              </div>
              <div className='BaseTable__header-cell-text'>Title</div>
            </>
          );
        }

        return React.cloneElement(cells[i], null);
      });
    }

    return _.map(columns, (column, i) => {
      let children = _.filter(cells[i].props.children, _.identity);
      let value = '';

      children[0] = value;

      if (column.key == 'name') {
        value = (
          <div className='BaseTable__header-cell-text'>{__('stages.sum')}</div>
        );
      }

      const minStartDateObject = _.minBy(data, (group) => group?.date?.start);
      const maxEndDateObject = _.maxBy(data, (group) => group?.date?.end);

      const start = _.get(minStartDateObject, 'date.start');
      const end = _.get(maxEndDateObject, 'date.end');

      const duration =
        start && end
          ? moment(end).startOf('day').diff(moment(start).startOf('day'), 'days') +
            1
          : 0;

      const elapsed = start
        ? moment().startOf('day').diff(moment(start).startOf('day'), 'days')
        : 0;

      const elapsed_percent =
        duration > 0 ? _.round((elapsed / duration) * 100, 2) : 0;

      const completed_hours = _.sumBy(
        data,
        (group) => group?.time_log?.completed_hours
      );

      const completed_progress = duration > 0 ? completed_hours / duration : 0;
      const remaining_time = duration - elapsed;

      if (column.key == 'date.start') {
        value = (
          <div className='BaseTable__header-cell-text'>
            {dateFrom(_.get(minStartDateObject, 'date.start'), true) || ''}
          </div>
        );
      }

      if (column.key == 'date.end') {
        value = (
          <div className='BaseTable__header-cell-text'>
            {dateFrom(maxEndDateObject?.date.end, true) || ''}
          </div>
        );
      }

      if (column.key == 'date.duration') {
        value = (
          <div className='BaseTable__header-cell-text'>
            {formatNumber(duration)}
          </div>
        );
      }

      if (column.key == 'schedule.elapsed_time') {
        value = (
          <div className='BaseTable__header-cell-text'>
            {formatNumber(elapsed)}
          </div>
        );
      }

      if (column.key == 'schedule.elapsed_percent') {
        value = (
          <div className='BaseTable__header-cell-text'>
            {formatNumber(Math.round(elapsed_percent))}
          </div>
        );
      }

      if (column.key == 'schedule.remaining_time') {
        value = (
          <div className='BaseTable__header-cell-text'>
            {formatNumber(remaining_time)}
          </div>
        );
      }

      if (column.key == 'status') {
        value = '';
      }

      return React.cloneElement(cells[i], null, value, children[1], children[2]);
    });
  };

  const columns = [
    {
      key: 'name',
      name: __('Name'),
      width: 300,
      minWidth: 300,
      resizable: true,
      sortable: false,
      frozen: 'left',
      cellRenderer: ({ rowData }) => (
        <>
          {rowData?.checked === true && (
            <CheckedIcon
              className={styles.checked}
              onClick={() => _handleCheckbox(rowData?.id)}
            />
          )}
          {rowData?.checked === false && (
            <UncheckedIcon
              className={classNames(
                styles.unchecked,
                checked.length > 0 && styles.visible
              )}
              onClick={() => _handleCheckbox(rowData?.id)}
            />
          )}
          {rowData?.checked === 'neutral' && (
            <NeutralIcon
              className={styles.neutral}
              onClick={() => _handleCheckbox(rowData?.id)}
            />
          )}

          <div className={styles.name}>
            <span>{rowData?.order}</span>
            <input
              className={styles.input}
              type='text'
              disabled
              readOnly={!isProjectAdministrator}
              key={rowData?.id + _.get(rowData.name, languageId)}
              defaultValue={_.get(rowData.name, languageId)}
              maxLength={70}
            />
            {/* <span
              className={styles.readStage}
              onClick={() => {
                rowData.level == 0
                  ? null
                  : null;
              }}
            >
              <ArrowDoubleRightSmallIcon />
            </span> */}
          </div>
        </>
      ),
    },
    {
      key: 'date.start',
      title: __('stages.date.start'),
      align: 'right',
      width: 105,
      resizable: true,
      sortable: false,
      cellRenderer: ({
        rowData: { name, date, id, level, time_log, status },
      }) => {
        const substage_id = _.last(_.split(id, '-'));
        const disabled = status === 'canceled';

        return (
          <NumericInput
            key={id + date.start}
            onBlur={(startDate) => {
              if (disabled) return;
              return onSubstageUpdate(substage_id, {
                name,
                date: {
                  ...date,
                  start: formatBackendDate(startDate),
                  duration: date?.duration + 'd',
                },
                time_log: {
                  ...time_log,
                  planned_time: time_log?.planned_time + ' h',
                },
              });
            }}
            dateFormat
            readOnly={level === 0 || disabled}
            defaultValue={dateFrom(date.start, true)}
          />
        );
      },
    },
    {
      key: 'date.end',
      title: __('stages.date.end'),
      align: 'right',
      width: 105,
      resizable: true,
      sortable: false,
      cellRenderer: ({
        rowData: { name, date, id, level, time_log, status },
      }) => {
        const substage_id = _.last(_.split(id, '-'));
        const disabled = status === 'canceled';
        const deadlineLocked = Number(date?.deadline) === 1;

        return (
          <NumericInput
            key={id + date.end}
            onBlur={(endDate) => {
              if (disabled || deadlineLocked) return;
              return onSubstageUpdate(substage_id, {
                name,
                date: {
                  ...date,
                  end: formatBackendDate(endDate),
                  duration: date?.duration + 'd',
                },
                time_log: {
                  ...time_log,
                  planned_time: time_log?.planned_time + ' h',
                },
              });
            }}
            dateFormat
            readOnly={level === 0 || disabled || deadlineLocked}
            defaultValue={dateFrom(date.end, true)}
          />
        );
      },
    },
    {
      key: 'date.deadline_date',
      title: __('deadline'),
      align: 'center',
      width: 75,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        const deadline_date = rowData?.date?.deadline_date;
        if (!deadline_date) return null;

        return (
          <div className={styles.deadlineCell}>
            <Tooltip text={dateFrom(deadline_date, true)}>
              <span className={styles.deadlineIconBox}>
                <WarningIcon className={styles.deadlineIcon} />
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      key: 'date.duration',
      title: __('stages.date.duration'),
      align: 'right',
      width: 91,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        const {
          status,
          id,
          level,
          date,
          name,
          time_log,
          date: { duration },
        } = rowData;

        const substage_id = _.last(_.split(id, '-'));
        const disabled = status === 'canceled';
        const deadlineLocked = Number(date?.deadline) === 1;

        return (
          <NumericInput
            key={id + duration}
            onBlur={(value) => {
              if (disabled || deadlineLocked) return;
              return onSubstageUpdate(substage_id, {
                name,
                date: {
                  ...date,
                  duration: value + 'd',
                },
                time_log: {
                  ...time_log,
                  planned_time: time_log?.planned_time + ' h',
                },
              });
            }}
            readOnly={level === 0 || disabled || deadlineLocked}
            defaultValue={duration}
          />
        );
      },
    },
    {
      key: 'schedule.elapsed_time',
      title: __('stages.schedule.elapsed_time'),
      align: 'right',
      width: 91,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) =>
        formatNumber(rowData?.schedule?.total_elapsed_time),
    },
    {
      key: 'schedule.elapsed_percent',
      title: __('stages.schedule.elapsed_percent'),
      align: 'right',
      width: 93,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) =>
        formatNumber(rowData?.schedule?.elapsed_percent),
    },
    {
      key: 'schedule.remaining_time',
      title: __('stages.schedule.remaining_time'),
      align: 'right',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) =>
        formatNumber(rowData?.schedule?.remaining_time),
    },
    {
      key: 'status',
      className: styles.statusCell,
      title: __('stages.columns.status'),
      overflow: true,
      width: 130,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        const { status } = rowData;
        return (
          <div
            className={classNames(styles.status, styles[_.camelCase(status)])}
            title={getLocalizedListValue(
              stage_statuses,
              status,
              'label',
              'value'
            )}
          >
            {getLocalizedListValue(stage_statuses, status, 'label', 'value')}
          </div>
        );
      },
    },
  ];

  return (
    <AutoSizer>
      {({ height, width }) => (
        <BaseTable
          ref={myRef}
          rowClassName={({ rowData }) =>
            classNames(
              rowData?.level === 0 && styles.darkGray,
              rowData?.level === 1 && styles.lightGray,
              rowData?.status === 'canceled' && styles.disabledRow,
              styles.row,
              rowData?.id === focusedId && styles.focused,
              rowData?.id === focusedId && styles.leftFloatingColumn
            )
          }
          fixed
          rowProps={({ rowData, rowIndex }) => ({
            index: rowIndex,
            id: rowData?.id,
            onClick: () => onFocusChange(rowData?.id),
            name: rowData?.name || '',
            level: rowData?.level,
            parentId: rowData?.parentId,
          })}
          components={{
            ExpandIcon,
          }}
          expandIconProps={({ rowData }) => ({
            depth: rowData?.level,
            id: rowData?.id,
          })}
          expandColumnKey='name'
          columns={columns}
          data={final_data}
          expandedRowKeys={expandedRows || []}
          width={width}
          height={height}
          onExpandedRowsChange={onExpandedRowsChange}
          ignoreFunctionInColumnCompare={false}
          headerHeight={[32, 32]}
          headerRenderer={headerRenderer}
          headerClassName={({ headerIndex }) =>
            classNames(headerIndex === 1 && styles.calculations)
          }
          rowHeight={32}
        />
      )}
    </AutoSizer>
  );
};

export default withRouter(ProjectPlanningTable);
